import React, { useState, useEffect, useContext, useRef } from 'react';
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import * as dayjs from 'dayjs'
import { useTranslation } from 'react-i18next';
import { postContentWithAction, putContentWithAction, getContentWithAction } from '../../apiConnect';
import { getSelectedFarmId, getSelectedLang, getSelectedYear } from '../../common';
import { formatPlannedDate, groupByKey, valueMax, dayJsFormatDate, groupByMarked, round, updateCalculativeArray } from '../../commonFunctions';
import NumberFormat from 'react-number-format';
import CalendarModal from './CalendarModal';
import Spinner from 'react-bootstrap/Spinner';
import { useAlert } from 'react-alert'
import AppContext from '../../AppContext';
import { ICONS } from '../../img/icons'
import Icon from '../../img/Icon'
import { OverlayTrigger, Tooltip, Badge } from 'react-bootstrap'

import '../../css/general.css'


const MarkTillageModal = (props) => {
    const { t } = useTranslation();
    const { auth } = useContext(AppContext);

    const [plannedTillings, setPlannedTillings] = useState([]);
    const [combinedTillings, setCombinedTillings] = useState([]);
    const [calendarShow, setCalendarShow] = useState(false);
    const [date, setDate] = useState(dayjs().format());
    const [contentIsLoading, setContentIsLoading] = useState(true);
    const [infoText, setInfoText] = useState('Ladataan tietoja');
    const [saveInProgress, setSaveInProgress] = useState(false);

    const [refreshComponents, setRefreshComponents] = useState(null);

    //For area and amount calculations, CCD = CalculationCombinedData
    const [cCD, setCCD] = useState([]);

    const alert = useAlert();

    /* TODO: Move this to constants and check their IDs */
    const allowIdsWithUnit = useRef([21, 22, 42, 51]);

    useEffect(() => {
        var plotIds = [];

        props.selectEvent.selectedPlots.forEach(p => {
            plotIds.push({ plotId: p.apNumber });
        });

        const promiseMod = new Promise((resolve, reject) => {
            postContentWithAction(auth.getAccessToken(), 'plotModification', 'plotModificationsForPlots', {
                models: plotIds, lang: getSelectedLang()
            }, (data) => {
                resolve(data);
            }).catch(e => {
                reject(Error("Promise rejected"));
            });
        });


        var farmId = getSelectedFarmId();
        var farmYear = getSelectedYear();
        /* Get assignments if not in props.
         * Used to find existing assignments for modifications if not updating
         * from the assignment modal.
         */
        const assignmentsPromise = new Promise((resolve, reject) => {
            // Check if assignments are already in props --> no need to fetch again
            if (props.assignments) {
                resolve(null);
            }
            else {
                getContentWithAction(auth.getAccessToken(), 'assignments', 'getAssignments', { farmId, farmYear }).then((data) => {
                    resolve(data);
                }).catch(e => {
                    reject(Error("Promise rejected"));
                });
            }
        });

        Promise.all([promiseMod, assignmentsPromise]).then(([modifications, assignments]) => {
            // If props.assigments is not null, we are in the assignments only "mode" and 
            // filter only the modifications that have assignments
            if (props.assignments) {
                modifications = modifications.filter(mod => props.assignments.find(a => a.operNo === mod.number))
            }
            else {
                modifications = modifications.filter((mod) => mod.actionDate || mod.plannedDate);
            }

            modifications.forEach(mod => {
                mod.plotData = props.selectEvent.selectedPlots.find(p => p.apNumber === mod.plotId);
                mod.assignment = props.assignments ? props.assignments.find(a => a.operNo === mod.number) : assignments.find(a => a.operNo === mod.number);
            });
            setPlannedTillings(modifications);

            var grouped1 = groupByKey(modifications, 'way');
            var grouped2 = [];
            for (const key in grouped1) {
                let m = groupByMarked(grouped1[key], 'actionDate', 'plannedDate');
                
                for (const type in m) {
                    for (const key in m[type]) { // Looping grouped keys
                        let combinedIds = [];
                        let combinedArea = 0;
                        let combinedCount = { min: m[type][key][0].count, max: m[type][key][0].count };
                        let combinedDepth = { min: m[type][key][0].depth, max: m[type][key][0].depth };

                        m[type][key].forEach(s => { // Looping ferts
                            combinedIds.push({
                                plotId: s.plotId,
                                number: s.number,
                                unitNumber: s.unitNumber,
                                plotName: s.plotData.name + ' - ' + s.plotData.apChar,
                                plotArea: s.plotData.area
                            });
                            if (s.area)
                                combinedArea += s.area;
                            else
                                combinedArea += s.plotData.area;
                            combinedCount.min = Math.min(combinedCount.min, s.count);
                            combinedCount.max = Math.max(combinedCount.max, s.count);

                            combinedDepth.min = Math.min(combinedDepth.min, s.depth);
                            combinedDepth.max = Math.max(combinedDepth.max, s.depth);
                        });
                        var combinedData = { ...m[type][key][0] }; // Copying first element for base data

                        if (combinedCount.min !== combinedCount.max) combinedData.count = null;
                        if (combinedDepth.min !== combinedDepth.max) combinedData.depth = null;

                        var tillageObject = {
                            date: key,
                            combinedData: combinedData,
                            combinedArea: combinedArea,
                            combinedIds: combinedIds,
                            combinedCount: combinedCount,
                            combinedDepth: combinedDepth,
                            unitNumber: combinedIds[0].unitNumber
                        };
                        
                        grouped2.push({
                            tilling: key,
                            combinedName: combinedData.description,
                            plannedDate: combinedData.plannedDate,
                            date: combinedData.actionDate,
                            objects: [tillageObject]
                        });
                    }
                }
            }

            // First sort all planned tillages by date, then by name
            var sortedTillages = grouped2.filter(s => s.plannedDate && !s.date).sort((a, b) => {
                if (a.plannedDate < b.plannedDate) return -1;
                if (a.plannedDate > b.plannedDate) return 1;
                if (a.combinedName < b.combinedName) return -1;
                if (a.combinedName > b.combinedName) return 1;
                return 0;
            });

            // Then sort all done tillages by name and append to the end of the list
            sortedTillages.push(...grouped2.filter(f => f.date).sort((a, b) => {
                if (a.combinedName < b.combinedName) return -1;
                if (a.combinedName > b.combinedName) return 1;
                if (a.date < b.date) return -1;
                if (a.date > b.date) return 1;
                return 0;
            }));

            setCCD(sortedTillages);
            setCombinedTillings(sortedTillages);

            setContentIsLoading(false);
            setInfoText('Ei suunniteltuja muokkauksia');
        }).catch(error => console.log(`Error in executing ${error}`));

    }, [props.selectEvent]);

    const updateDate = (value) => {
        var newDate = date;
        newDate.actionDate = value;
        setDate(newDate);
    };

    const saveTillings = () => {
        setSaveInProgress(true);
        let today = dayjs().format('YYYYMMDD');
        let invalidDate = false;
        var newTillings = [];

        combinedTillings.forEach(product => {
            product.objects.forEach(mod => {
                mod.combinedIds.forEach(plot => {
                    var originalData = plannedTillings.find(s => s.number === plot.number);
                    var data = mod.combinedData;
                    let formattedDate = dayjs(data.actionDate).format('YYYYMMDD');
                    if (data.actionDate && today < formattedDate) invalidDate = true; // Check for valid dates
                    if (data.actionDate) {
                        newTillings.push({
                            plannedDate: originalData.plannedDate,
                            count: data.count ? data.count : originalData.count,
                            depth: data.depth ? data.depth : originalData.depth,
                            way: data.way,
                            description: data.description,
                            product: data.product,
                            number: plot.number,
                            plotId: plot.plotId,
                            actionDate: data.actionDate,
                            unitType: data.unitType,
                            area: originalData.area ? originalData.area : plot.plotArea,
                            assignmentId: originalData.assignment ? originalData.assignment.id : 0
                        });
                    }
                });
            });
        });

        if (!invalidDate) {
            putContentWithAction(auth.getAccessToken(), 'plotModification', 'update', {
                models: newTillings
            }).then(() => {
                setSaveInProgress(false);
                props.setRefresh(Date.now());
                alert.show(t('SavedSuccesfully'), { type: 'success' });
            }).catch(e => {
                setSaveInProgress(false);
                alert.show(t('SavedFailed'), { type: 'error' });
            });

            props.onHide();
        }
        else {
            alert('invalid date')
        }
    }

    let checkShowDepth = (combinedRow) => {
        if (!allowIdsWithUnit.current.find(a => a === combinedRow.unitNumber)) {
            return true;
        }

        return false;
    }

    let depthValueValidator = (data, object) => {
        var value = data;
        if (value === null || value === undefined) return;
        if (value < 1)
            object.combinedData.depth = parseInt(0);
        else if (value > 99)
            object.combinedData.depth = parseInt(99);
        else 
            object.combinedData.depth = value;
    }

    let modCountValidator = (data, object) => {
        var value = data;
        if (value === null || value === undefined) return;
        if (value < 1) {
            object.combinedData.count = 0;
        }
        else if (value > 9) {
            object.combinedData.count = 9;
        }
        else {
           object.combinedData.count = value;
        }
    }

    return (
        <>
            <Modal show={props.show} onHide={props.onHide}>
                <Modal.Header closeButton>
                    <Modal.Title className="text-dark">{t('MarkTillageDone')}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-dark">
                    <Form>
                        {contentIsLoading ? <Spinner as="span" className="center" animation="border" role="status" variant="secondary" /> : ''}
                        {(combinedTillings) && (combinedTillings.length > 0) ? combinedTillings.map((product, i) =>
                            product.objects.map((mod, j) =>
                                <div key={i} id={i} className="mark-row-style border-bottom">
                                    <div className="mark-row-header">
                                        <b>{mod.combinedData.productName ? mod.combinedData.productName : mod.combinedData.description}</b>
                                        <br />
                                        {mod.combinedIds.length > 1 ?
                                            <OverlayTrigger trigger="click" rootClose placement="right" overlay={
                                                <Tooltip id="overlay">
                                                    {mod.combinedIds.slice(1).map((p, i) =>
                                                        <div key={p.plotName}>{p.plotName}</div>
                                                    )}
                                                </Tooltip>
                                            }>
                                                <Button size="sm" variant="light">
                                                    {mod.combinedIds[0].plotName} <b>+</b> <Badge pill bg="secondary">{mod.combinedIds.length - 1}</Badge>
                                                </Button>
                                            </OverlayTrigger>
                                            :
                                            mod.combinedIds.length === 1 ?
                                                <Button size="sm" variant="light" className="disabledButton">
                                                    {mod.combinedIds[0].plotName}
                                                </Button>
                                                : ''}
                                    </div>


                                    { /* NEW SECTION */ }
                                    <Form.Group controlId="formMeasure">
                                        <Form.Label className="text-dark">{t('Depth')} {mod.combinedData.unitText ? '(' + mod.combinedData.unitText + ')' : null}</Form.Label>
                                        <div className="row">
                                            <div className="col-3">
                                                <NumberFormat
                                                    className="form-control"
                                                    disabled={checkShowDepth(cCD[i].objects[j].combinedData)}
                                                    value={!checkShowDepth(cCD[i].objects[j].combinedData) && cCD[i].objects[j].combinedData.depth > 0 ? cCD[i].objects[j].combinedData.depth : ''}
                                                    decimalScale={2}
                                                    placeholder={!checkShowDepth(cCD[i].objects[j].combinedData) ? `${mod.combinedDepth.min} - ${mod.combinedDepth.max}` : ''}
                                                    fixedDecimalScale={false}
                                                    onValueChange={(value) => {
                                                        if (value.floatValue) {
                                                            depthValueValidator(value.floatValue, mod);
                                                            // Need to refresh components to show the changes
                                                            setRefreshComponents(Date.now());
                                                        }
                                                    }}
                                                    displayType={'input'}
                                                    thousandSeparator={' '}
                                                    isAllowed={valueMax}
                                                    allowNegative={false}
                                                    allowedDecimalSeparators={['.', ',']} />

                                            </div>
                                            <div className="col-9">
                                                <Form.Range className="tilling-form-range" min={1} max={99}
                                                    disabled={checkShowDepth(cCD[i].objects[j].combinedData)}
                                                    type="range"
                                                    onChange={(value) => {
                                                        if (value) {
                                                            depthValueValidator(value.target.value, mod);
                                                            // Need to refresh components to show the changes
                                                            setRefreshComponents(Date.now());
                                                        }
                                                    }}
                                                    value={cCD[i].objects[j].combinedData.depth > 0 ? cCD[i].objects[j].combinedData.depth : 0}
                                                    variant='success'
                                                />
                                            </div>
                                        </div>
                                    </Form.Group>

                                    <Form.Group controlId="formTimes">
                                        <Form.Label className="text-dark">{t('Sets')}</Form.Label>
                                        <div className="row">
                                            <div className="col-3">
                                                <NumberFormat
                                                    className="form-control"
                                                    value={cCD[i].objects[j].combinedData.count}
                                                    decimalScale={2}
                                                    placeholder={`${mod.combinedCount.min} - ${mod.combinedCount.max}`}
                                                    fixedDecimalScale={false}
                                                    onValueChange={(value) => {
                                                        if (value.floatValue) {
                                                            modCountValidator(value.floatValue, mod);
                                                            // Need to refresh components to show the changes
                                                            setRefreshComponents(Date.now());
                                                        }
                                                    }}
                                                    displayType={'input'}
                                                    thousandSeparator={' '}
                                                    isAllowed={valueMax}
                                                    allowNegative={false}
                                                    allowedDecimalSeparators={['.', ',']} />
                                            </div>
                                            <div className="col-9">
                                                <Form.Range className="tilling-form-range"
                                                    type="range"
                                                    onChange={(value) => {
                                                        if (value) {
                                                            modCountValidator(value.target.value, mod);
                                                            // Need to refresh components to show the changes
                                                            setRefreshComponents(Date.now());
                                                        }
                                                    }}
                                                    value={cCD[i].objects[j].combinedData.count > 0 ? cCD[i].objects[j].combinedData.count : 0}
                                                    min={1} max={9}
                                                />
                                            </div>
                                        </div>
                                    </Form.Group>

                                    { /* ============================================================================== */ }


                                    <div className="mark-row-content">
                                        <Form.Group controlId="fromDate">
                                            <Form.Label className="text-dark">{t('Date')}</Form.Label>
                                            <Form.Control className="form-control inline-form-field" type="text" onClick={() => {
                                                setDate(mod.combinedData);
                                                setCalendarShow(true);
                                            }}
                                                placeholder={formatPlannedDate(mod.combinedData.plannedDate, t)}
                                                value={mod.combinedData.actionDate ? dayJsFormatDate(mod.combinedData.actionDate) : ''}
                                                onChange={() => { }} />
                                        </Form.Group>
                                    </div>
                                </div>)
                        ) : <div>{infoText}</div>}
                    </Form>
                </Modal.Body>
                <Modal.Footer className="modal-footer-right-align">
                    <Button variant="outline-secondary" onClick={props.onHide}>{t('Cancel')}</Button>
                    <Button variant="success" disabled={saveInProgress} className="btn-show-more" onClick={() => { saveTillings(); }}>{saveInProgress ? <Spinner as="span" animation="border" size="sm" role="status" variant="secondary" /> : t('Save')}</Button>
                </Modal.Footer>
            </Modal>
            {calendarShow === true ?
                <CalendarModal
                    show={calendarShow}
                    onHide={() => setCalendarShow(false)}
                    onChange={updateDate}
                    selecteddate={date.date}
                />
                : ''}
        </>
    )
}

export default MarkTillageModal;